import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useVideoQuery } from "@/api/videos/queries/useVideoQuery";
import { Logo } from "@/components/brand/Logo";
import LoadingScreen from "@/components/feedback/LoadingScreen";
import { BasicLayout } from "@/components/layout/BasicLayout";
import { AssetsChat } from "@/components/new/video/chats/AssetsChat/AssetsChat";
import { VideoCreationChat } from "@/components/chat/VideoCreationChat";
import { AssetStudioPanel } from "@/components/chat/AssetStudioPanel";
import { RevisionPanel } from "@/components/chat/RevisionPanel";
import { useVideoScenesMutation } from "@/api/videos/useVideoScenesMutation";
import type { AssetPhase } from "@/components/chat/AssetStudioPanel";
import { useAcceptAssetMutation } from "@/api/videos/useAcceptAssetMutation";
import { useActivateAssetVersionMutation } from "@/api/videos/useActivateAssetVersionMutation";
import { useGenerateAssetMutation } from "@/api/videos/useGenerateAssetMutation";
import { useSkipAssetMutation } from "@/api/videos/useSkipAssetMutation";
import { useDeleteAssetMutation } from "@/api/videos/useDeleteAssetMutation";
import { xhrUploaderForAsset } from "@/api/uploader";
import { useConfirm } from "@/components/layout/confirm";
import toast from "@/components/feedback/toast";
import ScenesScreen from "@/components/new/video/ScenesScreen";
import { PlayerBar } from "@/components/player/PlayerBar";
import { PlayerSurface } from "@/components/player/PlayerSurface";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import { useTimelineStore } from "@/stores/timeline.store";
import { usePlayerStore } from "@/stores/video.store";

export type TabType = "scenes" | "studio";

const videoSearchSchema = z.object({
  from: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/v/$videoId/")({
  component: RouteComponent,
  validateSearch: videoSearchSchema,
});

function StudioPlaceholderCanvas({ width, height }: { width: number; height: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const style = getComputedStyle(canvas);
    const bgBase = style.getPropertyValue("--bg-base").trim();
    const isDark = bgBase.startsWith("#0") || (bgBase.startsWith("rgb") && parseInt(bgBase.split(",")[0].replace(/\D/g, "")) < 30);

    // Dark: vivid accent colors that glow. Light: soft pastels that float.
    const ar = isDark ? 99 : 165;   // indigo R
    const ag = isDark ? 102 : 180;  // indigo G
    const ab = isDark ? 241 : 252;  // indigo B
    const sr = isDark ? 159 : 196;  // violet R
    const sg = isDark ? 62 : 167;   // violet G
    const sb = isDark ? 255 : 255;  // violet B
    const boost = isDark ? 1 : 0.7; // light mode: softer alphas

    const cx = width / 2;
    const cy = height * 0.42;

    // ── Particles with gravity toward center ──
    type Particle = {
      x: number; y: number; vx: number; vy: number;
      size: number; alpha: number; life: number; maxLife: number;
      hue: number; // 0 = accent, 1 = secondary
    };
    const particles: Particle[] = [];
    for (let i = 0; i < 90; i++) {
      particles.push({
        x: Math.random() * width, y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2.5 + 0.5,
        alpha: Math.random() * 0.6 + 0.1,
        life: Math.random() * 400, maxLife: 400 + Math.random() * 300,
        hue: Math.random() > 0.6 ? 1 : 0,
      });
    }

    // ── Shooting stars ──
    type Meteor = { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; length: number };
    const meteors: Meteor[] = [];
    const spawnMeteor = () => {
      const angle = -Math.PI * 0.15 + (Math.random() - 0.5) * 0.4;
      const speed = 3 + Math.random() * 4;
      meteors.push({
        x: Math.random() * width * 0.8 + width * 0.1,
        y: -10,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle + Math.PI * 0.5) * speed,
        life: 0, maxLife: 40 + Math.random() * 30,
        length: 30 + Math.random() * 50,
      });
    };

    // ── Floating geometric shapes ──
    type Shape = { x: number; y: number; rotation: number; rotSpeed: number; size: number; sides: number; alpha: number; drift: number };
    const shapes: Shape[] = [];
    for (let i = 0; i < 6; i++) {
      shapes.push({
        x: Math.random() * width, y: Math.random() * height,
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.003,
        size: 15 + Math.random() * 25,
        sides: [3, 4, 5, 6][Math.floor(Math.random() * 4)],
        alpha: 0.03 + Math.random() * 0.04,
        drift: Math.random() * Math.PI * 2,
      });
    }

    // ── Light rays from center ──
    const numRays = 8;
    const rayAngles = Array.from({ length: numRays }, (_, i) => (i / numRays) * Math.PI * 2 + Math.random() * 0.3);

    // ── Grid ──
    const gridLines = 14;
    const gridSpacing = width / gridLines;

    let time = 0;
    let meteorTimer = 0;

    const draw = () => {
      time += 0.008;
      meteorTimer++;
      ctx.clearRect(0, 0, width, height);

      // === Nebula background blobs (boosted in light mode) ===
      const nb1x = cx + Math.sin(time * 0.3) * 60;
      const nb1y = cy + Math.cos(time * 0.25) * 40;
      const nebula1 = ctx.createRadialGradient(nb1x, nb1y, 0, nb1x, nb1y, 180);
      nebula1.addColorStop(0, `rgba(${ar},${ag},${ab},${0.06 * boost})`);
      nebula1.addColorStop(1, "transparent");
      ctx.fillStyle = nebula1;
      ctx.fillRect(0, 0, width, height);

      const nb2x = cx + Math.cos(time * 0.2) * 80;
      const nb2y = cy + Math.sin(time * 0.35) * 50;
      const nebula2 = ctx.createRadialGradient(nb2x, nb2y, 0, nb2x, nb2y, 150);
      nebula2.addColorStop(0, `rgba(${sr},${sg},${sb},${0.04 * boost})`);
      nebula2.addColorStop(1, "transparent");
      ctx.fillStyle = nebula2;
      ctx.fillRect(0, 0, width, height);

      // Third blob — cyan-shifted
      const nb3x = cx - Math.sin(time * 0.18) * 100;
      const nb3y = cy + Math.cos(time * 0.22) * 60;
      const nebula3 = ctx.createRadialGradient(nb3x, nb3y, 0, nb3x, nb3y, 120);
      nebula3.addColorStop(0, `rgba(${Math.max(0, ar - 30)},${Math.min(255, ag + 60)},${sb},${0.03 * boost})`);
      nebula3.addColorStop(1, "transparent");
      ctx.fillStyle = nebula3;
      ctx.fillRect(0, 0, width, height);

      // === Light rays ===
      ctx.save();
      ctx.globalCompositeOperation = isDark ? "lighter" : "source-over";
      for (let i = 0; i < numRays; i++) {
        const angle = rayAngles[i] + time * 0.05;
        const rayLen = 200 + 40 * Math.sin(time * 0.7 + i * 1.3);
        const rayAlpha = (0.015 + 0.01 * Math.sin(time + i * 0.9)) * boost;
        const ex = cx + Math.cos(angle) * rayLen;
        const ey = cy + Math.sin(angle) * rayLen;

        const grad = ctx.createLinearGradient(cx, cy, ex, ey);
        grad.addColorStop(0, `rgba(${ar},${ag},${ab},${rayAlpha * 2})`);
        grad.addColorStop(0.6, `rgba(${ar},${ag},${ab},${rayAlpha * 0.5})`);
        grad.addColorStop(1, "transparent");

        ctx.beginPath();
        const spread = 0.04;
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle - spread) * rayLen, cy + Math.sin(angle - spread) * rayLen);
        ctx.lineTo(cx + Math.cos(angle + spread) * rayLen, cy + Math.sin(angle + spread) * rayLen);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
      }
      ctx.restore();

      // === Perspective grid ===
      ctx.save();
      const vanishY = height * 0.35;
      for (let i = 0; i < 12; i++) {
        const t = i / 12;
        const y = vanishY + (height - vanishY) * Math.pow(t, 1.5);
        const squeeze = 1 - Math.pow(t, 2) * 0.3;
        const xLeft = cx - (width * 0.6 * squeeze);
        const xRight = cx + (width * 0.6 * squeeze);
        const alpha = (t * 0.6) * (0.04 + 0.02 * Math.sin(time + i * 0.5)) * boost;
        ctx.beginPath();
        ctx.moveTo(xLeft, y);
        ctx.lineTo(xRight, y);
        ctx.strokeStyle = `rgba(${ar},${ag},${ab},${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let i = -7; i <= 7; i++) {
        const baseX = cx + i * gridSpacing * 0.5;
        const topX = cx + i * 4;
        const alpha = (0.03 + 0.015 * Math.sin(time * 0.8 + i)) * boost;
        ctx.beginPath();
        ctx.moveTo(topX, vanishY);
        ctx.lineTo(baseX, height + 20);
        ctx.strokeStyle = `rgba(${ar},${ag},${ab},${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      ctx.restore();

      // === Central glow orb ===
      const glowR = 110 + 25 * Math.sin(time * 0.5);
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      glow.addColorStop(0, `rgba(${ar},${ag},${ab},${(0.14 + 0.06 * Math.sin(time)) * boost})`);
      glow.addColorStop(0.4, `rgba(${sr},${sg},${sb},${0.04 * boost})`);
      glow.addColorStop(1, "transparent");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      // === Ring pulses ===
      for (let r = 0; r < 4; r++) {
        const ringT = (time * 0.35 + r * 1.6) % 6.4;
        const ringR = ringT * 45;
        const ringA = Math.max(0, 0.12 - ringT * 0.019) * boost;
        if (ringA > 0) {
          ctx.beginPath();
          ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${ar},${ag},${ab},${ringA})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      // === Floating geometric shapes ===
      for (const s of shapes) {
        s.rotation += s.rotSpeed;
        s.x += Math.sin(time * 0.3 + s.drift) * 0.15;
        s.y += Math.cos(time * 0.25 + s.drift) * 0.1;
        if (s.x < -50) s.x = width + 50;
        if (s.x > width + 50) s.x = -50;
        if (s.y < -50) s.y = height + 50;
        if (s.y > height + 50) s.y = -50;

        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rotation);
        ctx.beginPath();
        for (let v = 0; v <= s.sides; v++) {
          const a = (v / s.sides) * Math.PI * 2 - Math.PI / 2;
          const px = Math.cos(a) * s.size;
          const py = Math.sin(a) * s.size;
          if (v === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${ar},${ag},${ab},${(s.alpha + 0.01 * Math.sin(time * 1.5 + s.drift)) * boost})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.restore();
      }

      // === Particles with gravity + constellation lines ===
      const connectionDist = 80;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        // Gentle gravity toward center
        const dx = cx - p.x;
        const dy = cy - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 30) {
          p.vx += (dx / dist) * 0.002;
          p.vy += (dy / dist) * 0.002;
        }
        // Damping
        p.vx *= 0.999;
        p.vy *= 0.999;
        p.x += p.vx;
        p.y += p.vy;
        p.life++;

        if (p.life > p.maxLife || p.x < -20 || p.x > width + 20 || p.y < -20 || p.y > height + 20) {
          const angle = Math.random() * Math.PI * 2;
          const r = Math.max(width, height) * 0.4 + Math.random() * 100;
          p.x = cx + Math.cos(angle) * r;
          p.y = cy + Math.sin(angle) * r;
          p.vx = (Math.random() - 0.5) * 0.3;
          p.vy = (Math.random() - 0.5) * 0.3;
          p.life = 0;
          p.maxLife = 400 + Math.random() * 300;
        }

        const lifeRatio = p.life / p.maxLife;
        const fade = lifeRatio < 0.1 ? lifeRatio / 0.1 : lifeRatio > 0.8 ? (1 - lifeRatio) / 0.2 : 1;
        const alpha = p.alpha * fade;
        const pr = p.hue === 0 ? ar : sr;
        const pg = p.hue === 0 ? ag : sg;
        const pb = p.hue === 0 ? ab : sb;

        // Glow around larger particles
        if (p.size > 1.5) {
          const pglow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
          pglow.addColorStop(0, `rgba(${pr},${pg},${pb},${alpha * 0.3 * boost})`);
          pglow.addColorStop(1, "transparent");
          ctx.fillStyle = pglow;
          ctx.fillRect(p.x - p.size * 4, p.y - p.size * 4, p.size * 8, p.size * 8);
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${pr},${pg},${pb},${alpha * boost})`;
        ctx.fill();

        // Constellation lines to nearby particles
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const ddx = p.x - q.x;
          const ddy = p.y - q.y;
          const d = Math.sqrt(ddx * ddx + ddy * ddy);
          if (d < connectionDist) {
            const lineAlpha = (1 - d / connectionDist) * 0.06 * fade * boost;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(${ar},${ag},${ab},${lineAlpha})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        }
      }

      // === Shooting stars ===
      if (meteorTimer % 120 === 0 && Math.random() > 0.4) spawnMeteor();
      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i];
        m.x += m.vx;
        m.y += m.vy;
        m.life++;
        if (m.life > m.maxLife) { meteors.splice(i, 1); continue; }

        const mFade = m.life < 5 ? m.life / 5 : m.life > m.maxLife - 10 ? (m.maxLife - m.life) / 10 : 1;
        const tailX = m.x - (m.vx / Math.sqrt(m.vx * m.vx + m.vy * m.vy)) * m.length;
        const tailY = m.y - (m.vy / Math.sqrt(m.vx * m.vx + m.vy * m.vy)) * m.length;

        const mGrad = ctx.createLinearGradient(tailX, tailY, m.x, m.y);
        mGrad.addColorStop(0, "transparent");
        mGrad.addColorStop(0.7, `rgba(${ar},${ag},${ab},${0.15 * mFade})`);
        mGrad.addColorStop(1, isDark ? `rgba(255,255,255,${0.4 * mFade})` : `rgba(${ar},${ag},${ab},${0.6 * mFade})`);

        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(m.x, m.y);
        ctx.strokeStyle = mGrad;
        ctx.lineWidth = isDark ? 1.5 : 1;
        ctx.stroke();

        // Head glow
        const hGlow = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, isDark ? 6 : 4);
        hGlow.addColorStop(0, isDark ? `rgba(255,255,255,${0.5 * mFade})` : `rgba(${ar},${ag},${ab},${0.5 * mFade})`);
        hGlow.addColorStop(1, "transparent");
        ctx.fillStyle = hGlow;
        ctx.fillRect(m.x - 6, m.y - 6, 12, 12);
      }

      // === Viewfinder corners ===
      const margin = 20;
      const cl = 18;
      const ca = (0.15 + 0.05 * Math.sin(time * 1.2)) * boost;
      const fr = isDark ? ar : 150;
      const fg = isDark ? ag : 150;
      const fb = isDark ? ab : 180;
      ctx.strokeStyle = `rgba(${fr},${fg},${fb},${ca})`;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(margin, margin + cl); ctx.lineTo(margin, margin); ctx.lineTo(margin + cl, margin); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(width - margin - cl, margin); ctx.lineTo(width - margin, margin); ctx.lineTo(width - margin, margin + cl); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(margin, height - margin - cl); ctx.lineTo(margin, height - margin); ctx.lineTo(margin + cl, height - margin); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(width - margin - cl, height - margin); ctx.lineTo(width - margin, height - margin); ctx.lineTo(width - margin, height - margin - cl); ctx.stroke();

      // === Center crosshair ===
      const chA = (0.12 + 0.05 * Math.sin(time * 2)) * boost;
      ctx.strokeStyle = `rgba(${fr},${fg},${fb},${chA})`;
      ctx.lineWidth = 0.6;
      ctx.beginPath(); ctx.moveTo(cx - 14, cy); ctx.lineTo(cx - 5, cy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx + 5, cy); ctx.lineTo(cx + 14, cy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy - 14); ctx.lineTo(cx, cy - 5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy + 5); ctx.lineTo(cx, cy + 14); ctx.stroke();
      // Outer circle
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${fr},${fg},${fb},${chA * 0.4})`;
      ctx.lineWidth = 0.4;
      ctx.stroke();

      // === Vignette (dark mode only) ===
      if (isDark) {
        const vignette = ctx.createRadialGradient(cx, height / 2, height * 0.25, cx, height / 2, height * 0.85);
        vignette.addColorStop(0, "transparent");
        vignette.addColorStop(1, "rgba(0,0,0,0.15)");
        ctx.fillStyle = vignette;
        ctx.fillRect(0, 0, width, height);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className="absolute inset-0"
    />
  );
}

function StudioPlaceholder({
  hasIncompleteAssets,
}: {
  hasIncompleteAssets?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 640, h: 360 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDims({ w: width, h: height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full flex items-center justify-center relative overflow-hidden rounded-xl aspect-video bg-base"
    >
      <StudioPlaceholderCanvas width={dims.w} height={dims.h} />

      {/* Logo watermark */}
      <div className="absolute top-3 left-4 flex items-center gap-1.5 pointer-events-none z-10">
        <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        <Logo size="sm" className="text-tertiary" />
      </div>

      {/* Timecode */}
      <div className="absolute top-3 right-4 pointer-events-none z-10">
        <span className="text-[9px] font-mono tabular-nums text-tertiary/40">
          00:00:00:00
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-xs">
        <div
          className={clsx(
            "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border backdrop-blur-sm",
            hasIncompleteAssets
              ? "bg-red-500/10 border-red-500/20"
              : "bg-surface border-border",
          )}
        >
          {hasIncompleteAssets ? (
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
        </div>
        <h3 className="text-sm font-medium text-primary tracking-tight">
          {hasIncompleteAssets
            ? "Uzupełnij grafiki referencyjne"
            : "Wygeneruj pierwszą scenę"}
        </h3>
        <p className="mt-1.5 text-xs text-tertiary leading-relaxed">
          {hasIncompleteAssets
            ? "Dodaj brakujące grafiki lub usuń puste assety, aby rozpocząć generowanie."
            : "Kliknij przycisk generowania w dolnym pasku, aby utworzyć pierwszą scenę."}
        </p>
      </div>
    </div>
  );
}

const CDN_BASE = "https://test-video-ai.b-cdn.net";

function StudioAssetEditor({
  videoId,
  assetId,
  assets,
  assetSuggestions,
  onClose,
  onFullscreen,
}: {
  videoId: string;
  assetId: string;
  assets: Array<{ id: string; status: string; refId: string; versions: Array<{ id: string; isActive: boolean; file?: { storagePath: string } | null }> }>;
  assetSuggestions: Array<{ assetId: string; refId: string; label: string; reason: string; sceneKeys: string[] }>;
  onClose: () => void;
  onFullscreen: (url: string, alt: string) => void;
}) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const asset = assets.find((a) => a.id === assetId);
  const suggestion = assetSuggestions.find((s) => s.assetId === assetId);

  const [phase, setPhase] = useState<AssetPhase>(() => {
    const hasImage = asset?.versions.some((v) => v.isActive && v.file?.storagePath);
    return hasImage ? "preview" : "choice";
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);

  const generateMutation = useGenerateAssetMutation({ videoId, assetId });
  const acceptMutation = useAcceptAssetMutation({ videoId });
  const activateVersionMutation = useActivateAssetVersionMutation({ videoId });
  const skipMutation = useSkipAssetMutation({ videoId });
  const deleteMutation = useDeleteAssetMutation({ videoId, assetId });

  const activeVersion = asset?.versions.find((v) => v.isActive);
  const activeImageUrl = activeVersion?.file?.storagePath
    ? `${CDN_BASE}/${activeVersion.file.storagePath}`
    : null;

  const versionsWithFiles = asset?.versions.filter((v) => v.file?.storagePath) ?? [];
  const allVersionUrls = versionsWithFiles.map((v) => `${CDN_BASE}/${v.file!.storagePath}`);
  const activeIdx = versionsWithFiles.findIndex((v) => v.isActive);

  if (!asset || !suggestion) return null;

  return (
    <AssetStudioPanel
      label={suggestion.label}
      reason={suggestion.reason}
      progress=""
      phase={phase}
      previewUrl={activeImageUrl}
      allVersionUrls={allVersionUrls}
      activeVersionIndex={activeIdx >= 0 ? activeIdx : 0}
      uploadProgress={uploadProgress}
      isGenerating={generateMutation.isPending}
      isSkipping={skipMutation.isPending}
      isDeleting={deleteMutation.isPending}
      isAccepted={asset.status === "accepted"}
      onGenerate={async (prompt) => {
        try {
          await generateMutation.mutateAsync({ prompt });
          setPhase("preview");
        } catch {
          toast({ message: "Nie udało się wygenerować grafiki.", type: "error" });
          setPhase("choice");
        }
      }}
      onUpload={async (file) => {
        setPhase("uploading");
        setUploadProgress(0);
        try {
          const uploader = xhrUploaderForAsset({ id: assetId });
          await uploader(file, (pct) => setUploadProgress(pct));
          await queryClient.invalidateQueries({ queryKey: ["video", videoId] });
          setPhase("preview");
        } catch {
          toast({ message: "Nie udało się wgrać pliku.", type: "error" });
          setPhase("choice");
        }
      }}
      onAccept={async () => {
        setPhase("accepting");
        try {
          const versions = asset.versions.filter((v) => v.file?.storagePath);
          const selected = versions[selectedVersionIndex];
          if (selected && !selected.isActive) {
            await activateVersionMutation.mutateAsync({ assetVersionId: selected.id });
          }
          await acceptMutation.mutateAsync({ assetId });
          onClose();
        } catch {
          setPhase("preview");
        }
      }}
      onSkip={() => confirm("Czy na pewno chcesz pominąć tę grafikę?", async () => {
        await skipMutation.mutateAsync({ assetId });
        onClose();
      })}
      onDelete={() => confirm("Czy na pewno chcesz usunąć tę grafikę?", async () => {
        await deleteMutation.mutateAsync();
        onClose();
      })}
      onRegenerate={() => setPhase("prompt")}
      onPhaseChange={setPhase}
      onImageClick={(url) => onFullscreen(url, suggestion.label)}
      onVersionChange={setSelectedVersionIndex}
      onClose={onClose}
    />
  );
}

function RouteComponent() {
  const { from } = useSearch({ from: "/_auth/v/$videoId/" });
  const { videoId } = Route.useParams();
  const navigate = useNavigate();

  // Consume `from=home` param — use it once, then remove from URL
  const isFromHomeRef = useRef(from === "home");
  useEffect(() => {
    if (from === "home") {
      void navigate({
        to: "/v/$videoId",
        params: { videoId },
        search: {},
        replace: true,
      });
    }
  }, [from, videoId, navigate]);

  const isFromHome = isFromHomeRef.current;
  const { data, activeNode, headNode } = useVideoQuery({ videoId });
  const windowWidth = useWindowWidth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileTab, setMobileTab] = useState<"player" | "scenes">("player");
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const revisionMutation = useVideoScenesMutation({ videoId });

  const activeRevision = data?.video.revisions.find(
    (revision) => !revision.accepted,
  );

  useEffect(() => {
    if (data?.video.scenes) {
      useTimelineStore.getState().setScenes(data.video.scenes);
    }
  }, [data?.video.scenes, videoId]);

  const hasScenes = (data?.video.scenes.length ?? 0) > 0;
  const allAssetsHandled =
    hasScenes &&
    (data?.video.assetSuggestions?.suggestions?.every((s) => {
      const asset = data?.video.assets?.find(
        (a: { id: string }) => a.id === s.assetId,
      );
      return (
        !asset || asset.status === "accepted" || asset.status === "skipped"
      );
    }) ??
      false);
  const needsAssetChat = hasScenes && !allAssetsHandled;

  // After finishing asset chat, open sidebar
  const prevNeededAssetChat = useRef(needsAssetChat);
  useEffect(() => {
    if (prevNeededAssetChat.current && !needsAssetChat && hasScenes) {
      setSidebarOpen(true);
    }
    prevNeededAssetChat.current = needsAssetChat;
  }, [needsAssetChat, hasScenes]);

  if (!data) {
    return <LoadingScreen />;
  }
  const videoStoragePath =
    activeNode?.file?.storagePath ?? headNode?.file?.storagePath ?? "";

  const hasIncompleteAssets = data.video.assets.some(
    (a) => !a.versions.some((v) => v.isActive && v.file),
  );

  const isCompact = windowWidth < 1024;

  return (
        needsAssetChat ? (
          <AssetsChat
            videoId={videoId}
            userPrompt={data.video.prompt}
            chosenStyle={data.video.styles?.options.find(
              (style) => style.id === data.video.style,
            )}
            chosenDuration={data.video.durationPlanner?.options.find(
              (option) =>
                option.durationSeconds === data.video.durationSeconds,
            )}
            assetSuggestions={
              data.video.assetSuggestions?.suggestions ?? []
            }
            assets={data.video.assets ?? []}
          />
        ) : hasScenes ? (
          <BasicLayout title="Studio">
            <div className="w-full h-full flex flex-col relative">
              {activeRevision && (
                <div className="bg-accent text-white py-2">
                  <div className="max-w-2xl px-4 md:px-6 lg:px-8 mx-auto">
                    <strong>Poprawka: </strong>
                    <span>{activeRevision.prompt}</span>
                  </div>
                </div>
              )}
              {(activeRevision?.warnings ?? []).length > 0 && (
                <div className="bg-red-500 text-white py-2">
                  <div className="max-w-2xl px-4 md:px-6 lg:px-8 mx-auto text-xs flex flex-col gap-2">
                    <strong>Ostrzezenie: </strong>
                    {activeRevision?.warnings.map((warning) => (
                      <span key={warning.id}>{warning.message}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden relative">
                {/* Mobile: tabs at top */}
                {isCompact && hasScenes && (
                  <div className="shrink-0 border-b border-border bg-surface flex">
                    {[
                      { id: "player" as const, label: "Player" },
                      { id: "scenes" as const, label: "Sceny" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setMobileTab(tab.id);
                          if (tab.id === "scenes") {
                            usePlayerStore.getState().pause();
                          }
                        }}
                        className={clsx(
                          "flex-1 flex items-center justify-center relative py-2.5 text-xs font-medium transition-colors",
                          mobileTab === tab.id
                            ? "text-accent"
                            : "text-tertiary",
                        )}
                      >
                        {tab.label}
                        {mobileTab === tab.id && (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-accent" />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div className="h-full flex flex-row">
                  {/* Scenes sidebar — mobile: tab-based full-width */}
                  {isCompact && mobileTab === "scenes" && (
                    <div className="h-full w-full shrink-0 overflow-y-auto scrollbar-thin pb-24">
                      <div className="w-full flex flex-col py-4">
                        <ScenesScreen videoId={videoId} onEditAsset={setEditingAssetId} />
                      </div>
                    </div>
                  )}

                  {/* Scenes sidebar — desktop: animated clip + always-visible edge strip */}
                  {!isCompact && (
                    <>
                      <div
                        className="h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-out"
                        style={{ width: sidebarOpen ? "min(640px, 50vw)" : "0px" }}
                      >
                        <div
                          className="h-full shrink-0 overflow-y-auto scrollbar-thin pb-24"
                          style={{ width: "min(640px, 50vw)" }}
                        >
                          <div className="w-full flex flex-col py-4">
                            <ScenesScreen videoId={videoId} onEditAsset={setEditingAssetId} />
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSidebarOpen((v) => !v)}
                        className="shrink-0 flex items-center justify-center w-8 h-full border-r border-border text-tertiary hover:text-secondary hover:bg-surface-hover transition-colors"
                        title={sidebarOpen ? "Ukryj scenariusz" : "Pokaż scenariusz"}
                      >
                        {sidebarOpen ? (
                          <ChevronLeft className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </button>
                    </>
                  )}

                  {/* Player — desktop: always visible; mobile: tab-based */}
                  <div
                    className={clsx(
                      "flex-1 min-w-0 flex flex-col h-full",
                      isCompact && mobileTab === "scenes" && "hidden",
                    )}
                  >
                    <div className={clsx(
                      "flex-1 relative min-h-0 overflow-hidden p-4 md:px-6 lg:px-8",
                      !isCompact && !sidebarOpen && "lg:pl-0",
                    )}>
                      {videoStoragePath ? (
                        <PlayerSurface
                          src={`https://test-video-ai.b-cdn.net/${videoStoragePath}`}
                        />
                      ) : (
                        <div className="flex-1 w-full h-full items-center justify-center flex">
                          <StudioPlaceholder
                            hasIncompleteAssets={hasIncompleteAssets}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Floating bottom panel — PlayerBar or AssetStudioPanel */}
                {editingAssetId ? (
                  <StudioAssetEditor
                    videoId={videoId}
                    assetId={editingAssetId}
                    assets={data.video.assets}
                    assetSuggestions={data.video.assetSuggestions?.suggestions ?? []}
                    onClose={() => setEditingAssetId(null)}
                    onFullscreen={(_url, _alt) => {/* TODO: fullscreen */}}
                  />
                ) : revisionOpen ? (
                  <RevisionPanel
                    isSubmitting={revisionMutation.isPending}
                    onSubmit={async (prompt) => {
                      try {
                        await revisionMutation.mutateAsync({ prompt });
                        setRevisionOpen(false);
                      } catch {
                        toast({ message: "Operacja nie powiodła się.", type: "error" });
                      }
                    }}
                    onClose={() => setRevisionOpen(false)}
                  />
                ) : (
                  <div className="absolute bottom-0 left-0 right-0 z-20 px-2 pb-safe md:px-6 lg:px-8 chat-bubble-enter">
                    <div className="bg-panel backdrop-blur-3xl rounded-2xl border border-accent/15 shadow-xl shadow-accent/5">
                      <div className="p-2.5 sm:p-3">
                        <PlayerBar
                          videoId={videoId}
                          activeScenes={isCompact ? mobileTab === "scenes" : sidebarOpen}
                          videoStoragePath={videoStoragePath}
                          onPlay={isCompact ? () => setMobileTab("player") : undefined}
                          onRevision={() => setRevisionOpen(true)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </BasicLayout>
        ) : (
          <BasicLayout title="Czat">
            <VideoCreationChat
              videoId={videoId}
              userPrompt={data.video.prompt}
              existingStyles={data.video.styles?.options}
              existingDurations={data.video.durationPlanner?.options}
              chosenStyleId={data.video.style ?? null}
              chosenDurationSeconds={data.video.durationSeconds ?? null}
              skipInitialAnimation={isFromHome}
            />
          </BasicLayout>
        )
  );
}
